import React from 'react';
import { Control, Controller, FieldError, FieldPath } from 'react-hook-form';

import {
  Radio,
  Theme,
  SxProps,
  FormLabel,
  RadioProps,
  RadioGroup,
  FormControl,
  useRadioGroup,
  FormHelperText,
  FormControlLabelProps,
} from '@mui/material';

import { Asterisk } from '@/components/Asterisk';

import {
  RadioBoxIcon,
  stylesRadioBox,
  RadioBoxCheckedIcon,
  stylesCrossingInfoBannerFormRadio,
  StyledFormControlLabel,
} from './SimpleRadioGroupField.styles';

type Option<T> = {
  value: T;
  disabled?: boolean;
  label: React.ReactNode;
};

type RadioGroupFieldProps<TFieldValues, T> = {
  id?: string;
  title?: string;
  options: Option<T>[];
  disabled?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
  isRequired?: boolean;
  defaultValue?: string;
  labelSx?: SxProps<Theme>;
  name?: FieldPath<TFieldValues>;
  control?: Control<TFieldValues>;
  error?: FieldError | undefined;
  onChange?: (value: T) => void;
};

export const SimpleRadioGroupField = <TFieldValues, T>({
  isRequired,
  disabled,
  options,
  control,
  labelSx,
  title,
  error,
  name,
  sx,
  id,
  onChange,
}: RadioGroupFieldProps<TFieldValues, T>) => (
  <FormControl error={Boolean(error)} sx={{ ...stylesCrossingInfoBannerFormRadio, ...sx }}>
    <FormLabel id='emptyPackaging' sx={labelSx}>
      {title}
      {isRequired ? <Asterisk /> : ''}
    </FormLabel>
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <RadioGroup {...field} row aria-labelledby='radio-buttons-group' name='radio-buttons-group'>
          {options.map(({ label, value }) => (
            <RadioBoxFormControlLabel
              id={id}
              sx={{ position: 'relative', ...sx }}
              key={String(value)}
              value={typeof value === 'string' ? value ?? '' : value}
              control={<RadioBox />}
              disabled={disabled}
              label={label}
              onChange={(e) => {
                field.onChange(e as React.ChangeEvent<Element>);
                onChange?.(value);
              }}
            />
          ))}
        </RadioGroup>
      )}
    />
    <FormHelperText>{error?.message}</FormHelperText>
  </FormControl>
);

function RadioBoxFormControlLabel(props: FormControlLabelProps) {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    // eslint-disable-next-line react/destructuring-assignment
    checked = radioGroup.value === String(props.value);
  }

  return <StyledFormControlLabel checked={checked} {...props} />;
}

function RadioBox(props: RadioProps) {
  return (
    <Radio
      disableRipple
      color='default'
      checkedIcon={<RadioBoxCheckedIcon />}
      icon={<RadioBoxIcon />}
      sx={stylesRadioBox}
      {...props}
    />
  );
}
